// Markdown renderer for agent messages.
// Copied from frontend/src/components/agent/AgentMarkdown.tsx.
// Uses react-markdown + remark-gfm. No Platinum-specific dependencies.
// CSS is inlined via sx prop / style instead of a .css import to avoid
// bundler configuration requirements. See ../../docs/90-provenance-map.md.

import React, { memo, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Box, useTheme } from '@mui/material'

import type { InlineCitationRendererFactory } from '../plugins.js'
import { HAIRLINE, INNER_RADIUS, PILL_RADIUS } from './surface.js'

interface AgentMarkdownProps {
  content: string
  /**
   * Host hook for inline citations. Given this message's whole markdown it
   * returns a renderer applied to every plain-text node, so the host can
   * number citations per message. Absent, or returning undefined, leaves the
   * output byte-for-byte as it was.
   */
  renderInlineCitations?: InlineCitationRendererFactory
}

/** Flatten a cell's children to plain text so we can spot numeric cells. */
export function cellText(children: React.ReactNode): string {
  if (children === null || children === undefined || typeof children === 'boolean') return ''
  if (typeof children === 'string' || typeof children === 'number') return String(children)
  if (Array.isArray(children)) return children.map(cellText).join('')
  if (React.isValidElement(children)) {
    return cellText((children.props as { children?: React.ReactNode }).children)
  }
  return ''
}

/**
 * True when a cell's trimmed text is a number — optionally with a leading sign,
 * thousands commas, decimals, or a trailing percent. Those cells are right-aligned.
 */
export function isNumericCellText(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return false
  return /^[+-]?(\d+|\d{1,3}(,\d{3})+)(\.\d+)?%?$/.test(trimmed)
}

/**
 * Tag the first element child of a row so the cell can pin itself. react-markdown
 * renders cells one at a time, so the row is the only place that knows which is first.
 */
function markFirstCell(children: React.ReactNode): React.ReactNode {
  let tagged = false
  return React.Children.map(children, child => {
    if (tagged || !React.isValidElement(child)) return child
    tagged = true
    return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, { 'data-first-col': 'true' })
  })
}

/** A same-app path (`/dashboards/...`) as opposed to an external URL. */
export function isInternalHref(href: string | undefined): boolean {
  if (!href) return false
  if (href.startsWith('/') && !href.startsWith('//')) return true
  if (typeof window !== 'undefined' && /^https?:\/\//i.test(href)) {
    try { return new URL(href).origin === window.location.origin } catch { return false }
  }
  return false
}

function AgentMarkdown({ content, renderInlineCitations }: AgentMarkdownProps) {
  const theme = useTheme()
  const renderText = useMemo(() => renderInlineCitations?.(content), [renderInlineCitations, content])

  // Applied to the children of the text-bearing elements. Without a host
  // renderer this returns the children untouched.
  const inline = (children: React.ReactNode): React.ReactNode => {
    if (!renderText) return children
    return React.Children.map(children, (child) =>
      typeof child === 'string' ? renderText(child) : child,
    )
  }

  // Opaque surfaces: the pinned first column paints over the scrolled cells, so
  // it cannot use a translucent tint.
  const headerBg = theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]
  const bodyBg = theme.palette.background.paper
  const hairline = HAIRLINE

  const cellBase: React.CSSProperties = {
    padding: '6px 10px',
    borderBottom: hairline,
    verticalAlign: 'top',
  }

  const pinned: React.CSSProperties = {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    borderRight: hairline,
  }

  return (
    <div
      className="agent-markdown"
      style={{
        fontSize: '0.8125rem',
        lineHeight: 1.6,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // A fenced block is a small card: the same edge and inner corner the
          // artifact previews use, on a soft ground, scrolling sideways rather
          // than wrapping. Inline code is left to the browser's monospace.
          pre: ({ children }) => (
            <Box
              component="pre"
              data-testid="markdown-code-block"
              sx={{
                m: 0,
                my: 1,
                p: '10px 12px',
                fontSize: 12,
                lineHeight: 1.55,
                overflowX: 'auto',
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#f8fafc',
                border: hairline,
                borderRadius: INNER_RADIUS,
                scrollbarWidth: 'thin',
              }}
            >
              {children}
            </Box>
          ),
          // A link into this app (a dashboard the agent just made, say) opens
          // in THIS tab: the login lives in the tab's session storage, so a
          // new tab would ask the reader to log in again. Anything external
          // still opens in a new tab.
          a: ({ children, href, ...props }) => (
            isInternalHref(href)
              ? <a href={href} {...props}>{children}</a>
              : <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
          ),
          table: ({ children, ...props }) => (
            <Box
              data-testid="markdown-table-scroll"
              sx={{
                // Inline-block so the frame hugs the table; max-width keeps it
                // inside the bubble and hands the overflow to the scroller.
                display: 'inline-block',
                verticalAlign: 'top',
                overflowX: 'auto',
                maxWidth: '100%',
                backgroundColor: bodyBg,
                border: hairline,
                borderRadius: INNER_RADIUS,
                my: 1,
                // Thin scrollbar so a wide table does not shout.
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.divider,
                  borderRadius: PILL_RADIUS,
                },
              }}
            >
              <table
                {...props}
                style={{
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  fontSize: 'inherit',
                  // Cells size to their content; the container scrolls.
                  width: 'auto',
                }}
              >
                {children}
              </table>
            </Box>
          ),
          tr: ({ children, ...props }) => <tr {...props}>{markFirstCell(children)}</tr>,
          th: ({ children, style, ...props }) => {
            const isFirst = (props as Record<string, unknown>)['data-first-col'] === 'true'
            return (
              <th
                {...props}
                style={{
                  ...cellBase,
                  backgroundColor: headerBg,
                  fontWeight: 600,
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  ...(isFirst ? { ...pinned, backgroundColor: headerBg } : {}),
                  ...style,
                }}
              >
                {children}
              </th>
            )
          },
          td: ({ children, style, ...props }) => {
            const isFirst = (props as Record<string, unknown>)['data-first-col'] === 'true'
            const numeric = isNumericCellText(cellText(children))
            return (
              <td
                {...props}
                style={{
                  ...cellBase,
                  backgroundColor: bodyBg,
                  ...(numeric ? { textAlign: 'right', whiteSpace: 'nowrap' } : {}),
                  ...(isFirst ? { ...pinned, backgroundColor: bodyBg } : {}),
                  ...style,
                }}
              >
                {inline(children)}
              </td>
            )
          },
          ...(renderText
            ? {
                p: ({ children }: { children?: React.ReactNode }) => <p>{inline(children)}</p>,
                li: ({ children }: { children?: React.ReactNode }) => <li>{inline(children)}</li>,
              }
            : {}),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

// Memoised: a message's markdown is parsed once per content, not once per
// re-render of the chat around it (a width change re-rendered every message).
export default memo(AgentMarkdown)
